export type VisualRedactionEffect = 'Blur' | 'Pixelate' | 'Blackout';
export type VisualRedactionShape = 'Rectangle' | 'Ellipse';
export type RedactionResizeHandle = 'nw' | 'ne' | 'sw' | 'se';

export type RedactionRegion = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type StoredVisualRedaction = {
  version: 1;
  kind: 'visual';
  effect: VisualRedactionEffect;
  shape: VisualRedactionShape;
  region: RedactionRegion;
  autoTrack: boolean;
};

export type StoredTranscriptRedaction = {
  version: 1;
  kind: 'transcript';
  transcriptMomentId?: string;
  transcriptTrackId?: string;
  startOffset?: number;
  endOffset?: number;
  text?: string;
  entireSpeaker?: boolean;
};

export type StoredRedaction = StoredVisualRedaction | StoredTranscriptRedaction;

const REDACTION_PREFIX = 'clipr:redaction:v1:';

export function serializeRedaction(redaction: StoredRedaction): string {
  return REDACTION_PREFIX + JSON.stringify(redaction);
}

export function parseRedaction(value?: string | null): StoredRedaction | null {
  if (!value?.startsWith(REDACTION_PREFIX))
    return null;

  try {
    const parsed = JSON.parse(value.slice(REDACTION_PREFIX.length)) as Partial<StoredRedaction>;
    if (parsed.version !== 1 || (parsed.kind !== 'visual' && parsed.kind !== 'transcript'))
      return null;

    return parsed as StoredRedaction;
  } catch {
    return null;
  }
}

export function selectionOffsetsToTimeRange(
  momentStart: number,
  momentEnd: number,
  textLength: number,
  startOffset: number,
  endOffset: number,
) {
  const duration = Math.max(momentEnd - momentStart, 0);
  const safeLength = Math.max(textLength, 1);
  const clampedStart = Math.max(0, Math.min(startOffset, safeLength));
  const clampedEnd = Math.max(clampedStart, Math.min(endOffset, safeLength));

  return {
    startTime: momentStart + duration * (clampedStart / safeLength),
    endTime: momentStart + duration * (clampedEnd / safeLength),
  };
}

export function normalizeRegion(start: { x: number; y: number }, end: { x: number; y: number }): RedactionRegion {
  const clamp = (value: number) => Math.max(0, Math.min(value, 1));
  const startX = clamp(start.x);
  const startY = clamp(start.y);
  const endX = clamp(end.x);
  const endY = clamp(end.y);

  return {
    x: Math.min(startX, endX),
    y: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  };
}

export function moveRegion(region: RedactionRegion, deltaX: number, deltaY: number): RedactionRegion {
  return {
    ...region,
    x: Math.max(0, Math.min(region.x + deltaX, 1 - region.width)),
    y: Math.max(0, Math.min(region.y + deltaY, 1 - region.height)),
  };
}

export function resizeRegion(
  region: RedactionRegion,
  point: { x: number; y: number },
  handle: RedactionResizeHandle,
  minSize = 0.02,
): RedactionRegion {
  let left = region.x;
  let right = region.x + region.width;
  let top = region.y;
  let bottom = region.y + region.height;

  if (handle.includes('w'))
    left = Math.max(0, Math.min(point.x, right - minSize));
  else
    right = Math.min(1, Math.max(point.x, left + minSize));

  if (handle.includes('n'))
    top = Math.max(0, Math.min(point.y, bottom - minSize));
  else
    bottom = Math.min(1, Math.max(point.y, top + minSize));

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };
}
